import React from 'react';
import {
    ActionIcon,
    Progress,
    Tooltip,
    Badge,
    Button,
    Text,
    Group,
    Stack,
    Box,
    UnstyledButton,
    Menu,
} from '@mantine/core';
import {
    IconArrowRight,
    IconArrowLeft,
    IconDotsVertical,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { SurveyZoneStoreCount } from './SurveyZoneStoreCount';
import { PriceSurvey } from '../../codegen/graphql';
import { useSurveyStore } from '../../stores/SurveyStore';
import { When } from '../common';
import { ExportCostOptionsButton } from '../common/ExportCostOptionsButton';
import {
    getProgressColor,
    getSurveyStatus,
    getSubmitToDivisionalManagerTooltipLabel,
    isSurveyInDivisionalManagerReview,
    isSurveyCancellable,
    getSurveyDivisionalManagerProgress,
    formatChampionSubmitToDivisionalManagerCopy,
    getSurveyScopeLabel,
    getSurveyScopeStores,
} from '../../pages/price-surveys/helpers';
import { STATUS_COLORS } from '../../pages/price-surveys/constants';
import { getSurveyTypeLabel } from '../../constants/surveyTypes';
import { toFinite } from '../../utility/toFinite';

type SurveyDetailsHeaderProps = {
    survey: PriceSurvey;
    onBack?: () => void;
    onExport?: (includeCostPricesAndMargins: boolean) => void;
    hideCostAndMargins?: boolean;
    onViewHistory?: () => void;
    onSubmit?: () => void;
    onSubmitSurvey?: () => void;
    canSubmitSurvey?: boolean;
    isSubmittingSurvey?: boolean;
    onReassign?: () => void;
    onPrepopulate?: () => void;
    canPrepopulate?: boolean;
    isPrepopulating?: boolean;
    prepopulateLabel?: string;
    onCancelSurvey?: () => void;
    exporting?: boolean;
    isDivisionalManagerReviewing?: boolean;
};

function SurveyDetailsHeader({
    survey,
    onBack,
    onExport,
    hideCostAndMargins = false,
    onViewHistory,
    onSubmit,
    onSubmitSurvey,
    canSubmitSurvey = false,
    isSubmittingSurvey = false,
    onReassign,
    onPrepopulate,
    canPrepopulate = false,
    isPrepopulating = false,
    prepopulateLabel = 'Prepop',
    onCancelSurvey,
    exporting = false,
    isDivisionalManagerReviewing = false,
}: SurveyDetailsHeaderProps) {
    const { completionPercent, completedItemsCount, totalItemsCount } =
        useSurveyStore();

    const {
        divisionalManagerActioned,
        divisionalManagerActionable,
        divisionalManagerPercent,
    } = getSurveyDivisionalManagerProgress(survey);

    const dmActioned = toFinite(divisionalManagerActioned);
    const dmActionable = toFinite(divisionalManagerActionable);
    const dmPercent = toFinite(divisionalManagerPercent);

    const showDivisionalManagerProgress =
        isDivisionalManagerReviewing ||
        isSurveyInDivisionalManagerReview(survey);

    const { progress, displayItemsDone, displayItemsTotal } =
        showDivisionalManagerProgress
            ? {
                  progress: dmPercent,
                  displayItemsDone: dmActioned,
                  displayItemsTotal: dmActionable,
              }
            : {
                  progress: completionPercent,
                  displayItemsDone: completedItemsCount,
                  displayItemsTotal: totalItemsCount,
              };

    const { color: progressColor } = getProgressColor(progress);

    const displayStatus = getSurveyStatus(
        survey?.status || 'NotStarted',
        survey?.dueBy || new Date(),
    );
    const statusColor = STATUS_COLORS[displayStatus] ?? 'gray';
    const zoneName = getSurveyScopeLabel(survey);
    const surveyTypeLabel = getSurveyTypeLabel(
        (survey as { surveyType?: string }).surveyType,
    );
    const surveyLabel = (survey as { label?: string }).label;
    const dueDate = survey?.dueBy
        ? dayjs(survey.dueBy).format('DD MMM YYYY')
        : '—';
    const championUser = survey?.championUser;
    const championStore = championUser?.managerStore;
    const championStoreName = championStore?.name?.trim() || null;
    const championStoreNo = championStore?.storeNo?.trim() || null;

    const championName = championUser
        ? [championUser.firstName, championUser.lastName]
              .filter(Boolean)
              .join(' ') || 'Unassigned'
        : 'Unassigned';
    const championSubmitToDivisionalManagerCopy =
        formatChampionSubmitToDivisionalManagerCopy(
            championUser
                ? [championUser.firstName, championUser.lastName]
                      .filter(Boolean)
                      .join(' ')
                : '',
            championStoreNo ?? undefined,
            championStoreName ?? undefined,
        );

    const submittedToDivisionalManager =
        isSurveyInDivisionalManagerReview(survey);
    const submitIncomplete = progress < 100;
    const reassignDisabled =
        survey?.status === 'DivisionalManagerReview' ||
        survey?.status === 'Completed' ||
        survey?.status === 'Cancelled';
    const canCancelSurvey = !!onCancelSurvey && isSurveyCancellable(survey);
    const canReassignSurvey = !!onReassign && !reassignDisabled;
    const showOverflowMenu = canCancelSurvey || canReassignSurvey;
    const submitTooltipLabel = getSubmitToDivisionalManagerTooltipLabel(
        submittedToDivisionalManager,
        submitIncomplete,
    );

    return (
        <Box className="w-full border-b border-gray-200">
            <Group
                justify="space-between"
                align="center"
                className="px-11 py-4"
                wrap="nowrap"
            >
                <Group gap="sm" align="center" wrap="nowrap">
                    <When condition={!!onBack}>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="lg"
                            onClick={onBack}
                            aria-label="Back to surveys"
                        >
                            <IconArrowLeft size={20} />
                        </ActionIcon>
                    </When>
                    <Stack gap={2}>
                        <Badge
                            color={statusColor}
                            variant="light"
                            radius="xl"
                            size="sm"
                            className="w-fit"
                        >
                            {displayStatus}
                        </Badge>

                        <Text
                            size="xl"
                            fw={700}
                            className="text-gray-900 m-0 leading-tight"
                        >
                            {zoneName}
                        </Text>

                        {(surveyTypeLabel || surveyLabel) && (
                            <Group gap="xs">
                                {surveyTypeLabel && (
                                    <Badge
                                        color="blue"
                                        variant="light"
                                        size="sm"
                                    >
                                        {surveyTypeLabel}
                                    </Badge>
                                )}
                                {surveyLabel && (
                                    <Text size="sm" c="dimmed">
                                        {surveyLabel}
                                    </Text>
                                )}
                            </Group>
                        )}

                        <SurveyZoneStoreCount
                            stores={getSurveyScopeStores(survey)}
                            championStoreId={
                                survey.championUser?.managerStore?.id ?? null
                            }
                        />
                    </Stack>
                </Group>

                <Group
                    gap="xl"
                    align="center"
                    wrap="nowrap"
                    className="flex-shrink-0"
                >
                    <Group gap={50} align="flex-start" wrap="nowrap">
                        <Stack gap={0}>
                            <Text size="xs" c="dimmed">
                                Survey Due:
                            </Text>
                            <Text size="sm" fw={600} className="text-gray-900">
                                {dueDate}
                            </Text>
                        </Stack>

                        <Tooltip
                            label={
                                <Stack gap={4}>
                                    <Text size="sm">
                                        {championStoreName ?? '—'}
                                    </Text>
                                    <Text size="xs" opacity={0.85}>
                                        Store no. {championStoreNo ?? '—'}
                                    </Text>
                                </Stack>
                            }
                            disabled={!championUser}
                            withArrow
                            position="bottom"
                            multiline
                            maw={280}
                        >
                            <Stack
                                gap={0}
                                align="flex-start"
                                className={
                                    championUser ? 'cursor-help' : undefined
                                }
                            >
                                <Text size="xs" c="dimmed">
                                    Champion:
                                </Text>
                                <Text
                                    size="sm"
                                    fw={600}
                                    className="text-gray-900"
                                >
                                    {championName}
                                </Text>
                                <When condition={canReassignSurvey}>
                                    <UnstyledButton
                                        onClick={onReassign}
                                        className="w-fit font-normal leading-normal cursor-pointer hover:underline"
                                    >
                                        <Text
                                            size="xs"
                                            c="blue"
                                            component="span"
                                        >
                                            Reassign Champion
                                            <IconArrowRight
                                                size={14}
                                                className="inline-block align-middle ml-0.5"
                                            />
                                        </Text>
                                    </UnstyledButton>
                                </When>
                            </Stack>
                        </Tooltip>

                        <Stack gap={4} className="min-w-[140px]">
                            <Group gap={4} align="baseline" wrap="nowrap">
                                {!onSubmit &&
                                progress >= 100 &&
                                !submittedToDivisionalManager &&
                                survey?.status !== 'Completed' ? (
                                    <Tooltip
                                        label={
                                            championSubmitToDivisionalManagerCopy
                                        }
                                        withArrow
                                        position="top"
                                        multiline
                                        maw={260}
                                    >
                                        <Text
                                            size="xl"
                                            fw={700}
                                            className="text-gray-900 cursor-help"
                                        >
                                            {progress}%
                                        </Text>
                                    </Tooltip>
                                ) : (
                                    <Text
                                        size="xl"
                                        fw={700}
                                        className="text-gray-900"
                                    >
                                        {progress}%
                                    </Text>
                                )}
                                <Text size="xs" c="dimmed">
                                    completed
                                </Text>
                            </Group>
                            <Progress
                                value={progress}
                                color={progressColor}
                                size="md"
                                radius="xl"
                            />
                            <Text size="xs" c="dimmed">
                                {displayItemsDone} of {displayItemsTotal} items
                            </Text>
                        </Stack>
                    </Group>

                    <Group gap={8} wrap="nowrap">
                        <When condition={!!onPrepopulate && canPrepopulate}>
                            <Button
                                variant="outline"
                                onClick={onPrepopulate}
                                loading={isPrepopulating}
                                disabled={isPrepopulating}
                                radius="xl"
                                className="border-orange-500 text-orange-600 hover:bg-orange-50 text-sm font-medium h-auto py-2.5 px-4 w-fit"
                            >
                                {prepopulateLabel}
                            </Button>
                        </When>

                        <Button
                            variant="outline"
                            onClick={onViewHistory}
                            rightSection={<IconArrowRight size={14} />}
                            radius="xl"
                            className="border-primary text-primary hover:bg-primary/10 text-sm font-medium h-auto py-2.5 px-4 w-fit"
                        >
                            View Activity History
                        </Button>

                        <ExportCostOptionsButton
                            label="Export"
                            loading={exporting}
                            disabled={exporting || !onExport}
                            hideCostAndMargins={hideCostAndMargins}
                            onExport={includeCostPricesAndMargins =>
                                onExport?.(includeCostPricesAndMargins)
                            }
                            variant="filled"
                            size="md"
                            radius="xl"
                            className="bg-primary text-white hover:bg-primary/90 border-0 text-sm font-medium h-auto py-2.5 px-4 w-fit"
                        />

                        <When
                            condition={
                                !!onSubmit &&
                                !submittedToDivisionalManager &&
                                survey?.status !== 'Completed' &&
                                survey?.status !== 'Cancelled'
                            }
                        >
                            <Tooltip
                                label={submitTooltipLabel}
                                disabled={progress >= 100}
                                withArrow
                                position="top"
                            >
                                <div>
                                    <Button
                                        onClick={onSubmit}
                                        radius="xl"
                                        disabled={submitIncomplete}
                                        className={clsx(
                                            'text-sm font-medium h-auto py-2.5 px-4 w-fit border-0',
                                            submitIncomplete
                                                ? 'bg-[#64748B] text-white cursor-not-allowed opacity-50'
                                                : 'bg-primary text-white hover:bg-primary/90',
                                        )}
                                    >
                                        Submit to Divisional Manager
                                    </Button>
                                </div>
                            </Tooltip>
                        </When>
                        <When condition={!!onSubmitSurvey}>
                            <Tooltip
                                label={
                                    canSubmitSurvey
                                        ? 'Submit survey and generate PCRs'
                                        : 'Action all items before submitting'
                                }
                                withArrow
                                position="top"
                            >
                                <div>
                                    <Button
                                        onClick={
                                            canSubmitSurvey
                                                ? onSubmitSurvey
                                                : undefined
                                        }
                                        radius="xl"
                                        disabled={
                                            !canSubmitSurvey ||
                                            isSubmittingSurvey
                                        }
                                        loading={isSubmittingSurvey}
                                        className={clsx(
                                            'text-sm font-medium h-auto py-2.5 px-4 w-fit border-0',
                                            canSubmitSurvey &&
                                                !isSubmittingSurvey
                                                ? 'bg-primary text-white hover:bg-primary/90'
                                                : 'bg-[#64748B] text-white cursor-not-allowed opacity-50',
                                        )}
                                    >
                                        Submit Survey
                                    </Button>
                                </div>
                            </Tooltip>
                        </When>

                        <When condition={showOverflowMenu}>
                            <Menu
                                shadow="md"
                                width={200}
                                position="bottom-end"
                                withinPortal
                            >
                                <Menu.Target>
                                    <ActionIcon
                                        variant="subtle"
                                        color="primary"
                                        size="lg"
                                        radius="xl"
                                        aria-label="More survey actions"
                                        className="text-primary hover:bg-primary/10 hover:text-primary"
                                    >
                                        <IconDotsVertical size={18} />
                                    </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <When condition={canReassignSurvey}>
                                        <Menu.Item onClick={onReassign}>
                                            Reassign Champion
                                        </Menu.Item>
                                    </When>
                                    <When condition={canCancelSurvey}>
                                        <Menu.Item
                                            color="red"
                                            onClick={onCancelSurvey}
                                        >
                                            Cancel Survey
                                        </Menu.Item>
                                    </When>
                                </Menu.Dropdown>
                            </Menu>
                        </When>
                    </Group>
                </Group>
            </Group>
        </Box>
    );
}

export default SurveyDetailsHeader;
